import React, { useState, useRef } from 'react';
import BilingualInput from './BilingualInput';
import FileUpload from './FileUpload';
import {
  tradeLicenseApi,
  applicantAuthApi,
  DOCUMENT_TYPES,
  OWNERSHIP_TYPES,
  APPLICATION_TYPES,
  GENDER_OPTIONS,
  INITIAL_FORM_DATA,
} from '../../services/tradeLicenseApi';

const STEPS = [
  'Applicant Information',
  'Business & License',
  'Location & Property',
  'Contact & Identity',
  'Attachments',
  'Review & Submit',
];

const ApplicationForm = ({
  categories = [],
  onSuccess,
  officeMode = false,
  initialData = {},
  lockApplicationType = false,
  readOnlyFields = [],
}) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA, ...initialData });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [photograph, setPhotograph] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const submittedRef = useRef(false);

  const isReadOnly = (field) => readOnlyFields.includes(field);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file for photograph.');
      return;
    }
    setPhotograph(file);
    setError(null);
  };

  const getRequiredDocTypes = () =>
    DOCUMENT_TYPES.filter((d) => d.required && d.type !== 'Passport_Photo').map((d) => d.type);

  const validateAttachments = () => {
    if (!photograph) {
      setError('Applicant photograph is required.');
      return false;
    }
    const requiredTypes = getRequiredDocTypes();
    const uploadedTypes = [...new Set(documents.map((d) => d.docType))];
    const missing = requiredTypes.filter((t) => !uploadedTypes.includes(t));
    if (missing.length) {
      const labels = missing.map((t) => {
        const meta = DOCUMENT_TYPES.find((d) => d.type === t);
        return meta ? meta.name_en : t;
      });
      setError(`Please upload required documents: ${labels.join(', ')}`);
      return false;
    }
    return true;
  };

  const validateStep = () => {
    if (step === 0) {
      if (!formData.applicant_first_name_en || !formData.applicant_gender) return false;
      if (!photograph) {
        setError('Applicant photograph is required.');
        return false;
      }
      return true;
    }
    if (step === 1) {
      if (formData.application_type === 'renewal' && !formData.existing_license_number) {
        setError('Existing license number is required for renewal.');
        return false;
      }
      return formData.business_name_en && formData.trade_category && formData.ownership_type;
    }
    if (step === 2) {
      return formData.ward_number && formData.address_en;
    }
    if (step === 3) {
      return formData.applicant_aadhar && formData.applicant_pan && formData.applicant_phone;
    }
    if (step === 4) {
      return validateAttachments();
    }
    return true;
  };

  const silentRegisterApplicant = async () => {
    if (!formData.applicant_phone || formData.applicant_phone.length < 10) return;
    try {
      await applicantAuthApi.silentRegister({
        phone: formData.applicant_phone,
        first_name: formData.applicant_first_name_en,
        last_name: formData.applicant_last_name_en || '',
        email: formData.applicant_email || '',
      });
    } catch {
      // Non-blocking: application can still proceed without account link
    }
  };

  const nextStep = async () => {
    setError(null);
    if (!validateStep()) {
      if (!error) setError('Please fill all required fields.');
      return;
    }
    if (step === 3 && !officeMode) {
      await silentRegisterApplicant();
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const prevStep = () => {
    setError(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  const uploadDocuments = async (appId) => {
    for (const doc of documents) {
      const docMeta = DOCUMENT_TYPES.find((d) => d.type === doc.docType) || {};
      const formDataUpload = new FormData();
      formDataUpload.append('application', appId);
      formDataUpload.append('document_type', doc.docType);
      formDataUpload.append('name_en', docMeta.name_en || doc.name);
      formDataUpload.append('name_hi', docMeta.name_hi || '');
      formDataUpload.append('file', doc.file);
      await tradeLicenseApi.uploadDocument(formDataUpload);
    }
  };

  const parseApiError = (err) => {
    const data = err.response?.data;
    if (!data) return err.message;
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    return Object.entries(data)
      .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(' ') : val}`)
      .join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== STEPS.length - 1) return;
    if (submittedRef.current || submitting) return;

    setError(null);
    if (!validateAttachments()) return;

    setSubmitting(true);
    submittedRef.current = true;

    try {
      const payload = { ...formData };
      let response;

      if (photograph) {
        const photoForm = new FormData();
        Object.entries(payload).forEach(([key, val]) => {
          if (val !== '' && val !== null && val !== undefined) photoForm.append(key, val);
        });
        photoForm.append('applicant_photograph', photograph);
        response = await tradeLicenseApi.createApplication(photoForm);
      } else {
        response = await tradeLicenseApi.createApplication(payload);
      }

      const appId = response.data.sid || response.data.id;

      // Upload documents with better error handling
      if (documents.length) {
        try {
          await uploadDocuments(appId);
        } catch (docErr) {
          console.error('Document upload failed:', docErr);
          setError('Application created but some documents failed to upload. You can upload them later from the application details.');
          // Don't throw - allow submission to proceed
        }
      }

      // Submit application
      try {
        const submitRes = await tradeLicenseApi.submitApplication(appId);
        onSuccess?.(submitRes.data);
      } catch (submitErr) {
        console.error('Submission failed:', submitErr);
        const errorMsg = parseApiError(submitErr);
        setError(`Application created successfully but submission failed: ${errorMsg}. Please try submitting again from your application dashboard. Application ID: ${appId}`);
      }
    } catch (err) {
      submittedRef.current = false;
      const errorMsg = parseApiError(err);
      // Check if it's a duplicate error
      if (errorMsg.includes('already exists')) {
        setError(errorMsg + ' If you have a draft application, please complete or delete it before creating a new one.');
      } else {
        setError('Error submitting application: ' + errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const selectedCategory = categories.find((c) => String(c.id) === String(formData.trade_category));
  const uploadedTypes = [...new Set(documents.map((d) => d.docType))];

  const renderReviewValue = (label, value, hiValue) => (
    <div className="review-row" key={label}>
      <span className="review-label">{label}</span>
      <span className="review-value">
        {value || '—'}
        {hiValue && <span className="review-hi"> ({hiValue})</span>}
      </span>
    </div>
  );

  return (
    <div className="tl-form-container tl-form-wide">
      <div className="form-header">
        <h2>{officeMode ? 'Create New Application (Office)' : 'Apply for Trade License'}</h2>
        <p>Complete all sections. Hindi fields auto-fill when you type in English.</p>
      </div>

      <div className="form-step-indicator">
        {STEPS.map((label, i) => (
          <span key={label} className={`step-dot ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            {i + 1}. {label}
          </span>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
        {step === 0 && (
          <div className="form-section">
            <h3>Applicant Information</h3>
            <BilingualInput
              label="Applicant Name"
              enName="applicant_first_name_en"
              hiName="applicant_first_name_hi"
              enValue={formData.applicant_first_name_en}
              hiValue={formData.applicant_first_name_hi}
              onChange={handleChange}
              required
              placeholderEn="Enter applicant full name"
              placeholderHi="आवेदक का पूरा नाम"
            />
            <div className="form-group">
              <label>Gender *</label>
              <select name="applicant_gender" value={formData.applicant_gender} onChange={handleChange} required>
                <option value="">Select gender</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g.value} value={g.value}>{g.label} / {g.label_hi}</option>
                ))}
              </select>
            </div>
            <BilingualInput
              label="Father/Husband Name"
              enName="applicant_father_husband_name_en"
              hiName="applicant_father_husband_name_hi"
              enValue={formData.applicant_father_husband_name_en}
              hiValue={formData.applicant_father_husband_name_hi}
              onChange={handleChange}
              placeholderEn="Father or husband name"
              placeholderHi="पिता/पति का नाम"
            />
            <div className="form-group">
              <label>Applicant Photograph *</label>
              <input type="file" accept="image/*" onChange={handlePhotoChange} required={!photograph} />
              {photograph && <small className="file-selected">Selected: {photograph.name}</small>}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="form-section">
            <h3>Business & License Information</h3>
            <div className="form-group">
              <label>Application Type *</label>
              <select
                name="application_type"
                value={formData.application_type}
                onChange={handleChange}
                required
                disabled={lockApplicationType}
              >
                {APPLICATION_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label} / {t.label_hi}</option>
                ))}
              </select>
            </div>
            {formData.application_type === 'renewal' && (
              <div className="form-group">
                <label>Existing License Number *</label>
                <input
                  type="text"
                  name="existing_license_number"
                  value={formData.existing_license_number}
                  onChange={handleChange}
                  required
                  readOnly={isReadOnly('existing_license_number')}
                  placeholder="Enter existing license number"
                />
              </div>
            )}
            <BilingualInput
              label="Business Name"
              enName="business_name_en"
              hiName="business_name_hi"
              enValue={formData.business_name_en}
              hiValue={formData.business_name_hi}
              onChange={handleChange}
              required
              placeholderEn="Business name"
              placeholderHi="व्यापार का नाम"
            />
            <div className="form-group">
              <label>Trade Category *</label>
              <select name="trade_category" value={formData.trade_category} onChange={handleChange} required>
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name_en} / {cat.name_hi}</option>
                ))}
              </select>
            </div>
            {selectedCategory && (
              <div className="form-group">
                <label>Business Type</label>
                <input type="text" value={selectedCategory.business_type} readOnly className="readonly-field" />
              </div>
            )}
            <div className="form-group">
              <label>Ownership Type *</label>
              <select name="ownership_type" value={formData.ownership_type} onChange={handleChange} required>
                <option value="">Select ownership type</option>
                {OWNERSHIP_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} / {o.label_hi}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="form-section">
            <h3>Location & Property Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Holding Number (Optional)</label>
                <input type="text" name="holding_number" value={formData.holding_number} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Ward Number *</label>
                <input type="text" name="ward_number" value={formData.ward_number} onChange={handleChange} required />
              </div>
            </div>
            <BilingualInput
              label="Full Address"
              enName="address_en"
              hiName="address_hi"
              enValue={formData.address_en}
              hiValue={formData.address_hi}
              onChange={handleChange}
              required
              multiline
              placeholderEn="Complete business address"
              placeholderHi="पूरा व्यापार पता"
            />
            <BilingualInput
              label="Property Owner Name"
              enName="property_owner_name_en"
              hiName="property_owner_name_hi"
              enValue={formData.property_owner_name_en}
              hiValue={formData.property_owner_name_hi}
              onChange={handleChange}
              placeholderEn="Property owner name"
              placeholderHi="संपत्ति स्वामी का नाम"
            />
            <BilingualInput
              label="Property Owner Address"
              enName="property_owner_address_en"
              hiName="property_owner_address_hi"
              enValue={formData.property_owner_address_en}
              hiValue={formData.property_owner_address_hi}
              onChange={handleChange}
              multiline
              placeholderEn="Property owner address"
              placeholderHi="संपत्ति स्वामी का पता"
            />
          </div>
        )}

        {step === 3 && (
          <div className="form-section">
            <h3>Contact & Identity Information</h3>
            <div className="form-group">
              <label>Aadhaar Number *</label>
              <input type="text" name="applicant_aadhar" value={formData.applicant_aadhar} onChange={handleChange} required maxLength={12} pattern="[0-9]{12}" placeholder="12-digit Aadhaar" />
            </div>
            <div className="form-group">
              <label>PAN Number *</label>
              <input type="text" name="applicant_pan" value={formData.applicant_pan} onChange={handleChange} required maxLength={10} placeholder="PAN number" />
            </div>
            <div className="form-group">
              <label>Mobile Number *</label>
              <input type="tel" name="applicant_phone" value={formData.applicant_phone} onChange={handleChange} required placeholder="10-digit mobile" />
            </div>
            <div className="form-group">
              <label>Email ID (Optional)</label>
              <input type="email" name="applicant_email" value={formData.applicant_email} onChange={handleChange} placeholder="Email address" />
            </div>
            <div className="form-group">
              <label>GST Number (Optional)</label>
              <input type="text" name="gst_number" value={formData.gst_number} onChange={handleChange} placeholder="GST number if applicable" />
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="form-section">
            <h3>Attachments</h3>
            <p className="section-hint">Upload all required documents. Drag & drop or click to browse.</p>
            {DOCUMENT_TYPES.filter((d) => d.required && d.type !== 'Passport_Photo').map((doc) => (
              <FileUpload
                key={doc.type}
                label={`${doc.name_en} / ${doc.name_hi}`}
                docType={doc.type}
                files={documents}
                onFilesChange={setDocuments}
                required
                multiple={false}
              />
            ))}
            <FileUpload
              label="Additional Supporting Documents / अतिरिक्त सहायक दस्तावेज़"
              docType="Supporting_Document"
              files={documents}
              onFilesChange={setDocuments}
            />
            <div className="attachment-checklist">
              <p><strong>Photograph:</strong> {photograph ? `✓ ${photograph.name}` : '✗ Not uploaded'}</p>
              {getRequiredDocTypes().map((type) => {
                const meta = DOCUMENT_TYPES.find((d) => d.type === type);
                const done = uploadedTypes.includes(type);
                return (
                  <p key={type}>
                    <strong>{meta?.name_en}:</strong> {done ? '✓ Uploaded' : '✗ Pending'}
                  </p>
                );
              })}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="form-section">
            <h3>Review & Submit</h3>
            <p className="section-hint">Please verify all details before final submission.</p>

            <div className="review-summary review-full">
              <h4>Applicant</h4>
              {renderReviewValue('Name', formData.applicant_first_name_en, formData.applicant_first_name_hi)}
              {renderReviewValue('Gender', formData.applicant_gender)}
              {renderReviewValue('Father/Husband', formData.applicant_father_husband_name_en, formData.applicant_father_husband_name_hi)}
              {renderReviewValue('Photograph', photograph?.name)}

              <h4>Business & License</h4>
              {renderReviewValue('Application Type', formData.application_type?.replace(/_/g, ' '))}
              {formData.existing_license_number && renderReviewValue('Existing License', formData.existing_license_number)}
              {renderReviewValue('Business Name', formData.business_name_en, formData.business_name_hi)}
              {renderReviewValue('Category', selectedCategory?.name_en)}
              {renderReviewValue('Ownership', formData.ownership_type)}

              <h4>Location</h4>
              {renderReviewValue('Ward', formData.ward_number)}
              {renderReviewValue('Holding', formData.holding_number)}
              {renderReviewValue('Address', formData.address_en, formData.address_hi)}
              {renderReviewValue('Property Owner', formData.property_owner_name_en, formData.property_owner_name_hi)}

              <h4>Contact & Identity</h4>
              {renderReviewValue('Aadhaar', formData.applicant_aadhar ? `XXXX-XXXX-${formData.applicant_aadhar.slice(-4)}` : '')}
              {renderReviewValue('PAN', formData.applicant_pan)}
              {renderReviewValue('Mobile', formData.applicant_phone)}
              {renderReviewValue('Email', formData.applicant_email)}
              {renderReviewValue('GST', formData.gst_number || '—')}

              <h4>Documents ({documents.length})</h4>
              {documents.length === 0 ? (
                <p className="review-empty">No supporting documents listed.</p>
              ) : (
                documents.map((doc, idx) => (
                  <div key={`${doc.docType}-${idx}`} className="review-doc-item">
                    <span>{DOCUMENT_TYPES.find((d) => d.type === doc.docType)?.name_en || doc.docType}</span>
                    <span>{doc.name}</span>
                    {doc.preview && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => window.open(doc.preview, '_blank')}
                      >
                        👁 View
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <div className="form-actions">
          {step > 0 && (
            <button type="button" className="btn btn-secondary" onClick={prevStep}>← Back</button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={nextStep}>Next →</button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
